import { FileTranscripts, LibraryFile } from './types';

export const timeToSeconds = (timestamp: string): number => {
  const [hours, minutes, seconds] = timestamp.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const getPromptText = (
  fileTranscripts: FileTranscripts,
  files: LibraryFile[],
  directions: string
) => {
  let transcriptText = '';
  Object.entries(fileTranscripts).forEach(([fileId, transcript], index) => {
    const file = files.find((f) => f._id === fileId);
    const fileName = file?.name || 'Unknown File';
    const date = file?.createdAt
      ? new Date(file.createdAt).toISOString().split('T')[0]
      : 'Unknown Date';

    // Parse the transcript JSON
    const parsedTranscript = JSON.parse(transcript);
    const segments = parsedTranscript.segments || [];

    // Convert segments to XML format with speaker and timestamp info
    const textWithSegments = segments
      .map((segment: any) => {
        const startTime = Math.floor(segment.start);
        const speaker = segment.speaker || 'UNKNOWN';
        const text = segment.text.trim();

        return `<segment time="${startTime}" speaker="${speaker}">${text}</segment>`;
      })
      .join('\n');

    transcriptText += `<transcript 
      number="${index + 1}" 
      id="${fileId}"
      filename="${fileName}" 
      date="${date}"
    >${textWithSegments}</transcript>\n`;
  });

  const transcriptCount = Object.keys(fileTranscripts).length;

  return `
<prompt>
  <context>
    You are an expert analyst and knowledge synthesizer. Your task is to analyze recordings and extract key insights with precision and clarity.
    
    ⚠️ PRIORITY INSTRUCTION:
    - Focus FIRST on directly answering the question/task provided
    - Provide a clear, response to the specific request
    - Only after addressing the main task, incorporate additional context and supporting details
    - NEVER wrap your entire response in a markdown code block
    - Write your response as plain text with markdown formatting
    - DO NOT start your response with \`\`\`markdown or any other code block indicator
  </context>

  <task>${directions}</task>

  <input>
    You have ${transcriptCount} recordings to analyze. Each recording is provided in XML format with metadata including:
    - id: unique identifier for the recording
    - filename: name of the recording
    - date: when the recording was created
    - segments: transcript text with timestamps and speaker information
  </input>

  <guidelines>
    1. Primary Response:
       - Begin with a direct answer to the task/question
       - Provide immediate, relevant insights
       - Be concise but complete in addressing the main request
       - Only then proceed with supporting details and additional context

    2. Structure & Format:
       - Use clear Markdown headers (##, ###) to organize sections
       - Create bulleted lists for discrete points
       - Bold key terms and important insights using **bold text**
       - Italicize for emphasis using *italic text*
       - Include relevant quotes using > for blockquotes
       - Organize insights by topic and importance
       - Cite sources using markdown links with recording IDs and speakers
       - DO NOT use any code blocks or backticks
       - Use plain text for all content
       - For technical terms or commands, use *italic* or **bold** formatting
       - Format lists with either:
         - Hyphens (-)
         - Numbers (1., 2., etc.)
       - Use > for important quotes or callouts
       - Indent sub-points with spaces
       - Use horizontal rules (---) to separate major sections

    3. Content Focus:
       - Focus on essential insights and key takeaways
       - Prioritize actionable information
       - Avoid redundancy and unnecessary detail
       - Include only the most relevant quotes that drive understanding
       - Combine related points to reduce length without losing meaning

    4. Output Structure:
       - Begin with a brief, focused summary
       - Group related insights together
       - Use clear headers for different sections
       - Include quotes sparingly - only when they provide unique value
       - When referring to recordings:
         - Use the filename if it's descriptive (e.g., "Q4 Strategy Meeting")
         - For non-descriptive filenames, use a clear identifier based on content
         - Be consistent with the chosen identifier throughout
         - Never use dates or recording numbers as primary identifiers

    5. Citation and Timestamp Validation Rules:
       ⚠️ CRITICAL: Citation Format
       - Citations MUST use the recording's ID, not the filename
       - ALWAYS use the format: [→](file_id@seconds)
         - Correct: [→](abc123@42)
         - Incorrect: [→](Meeting Recording@42) or [→](filename@42)
         - Common Error to Avoid: [→](abc123@42] (using ] instead of ))
       
       ⚠️ CRITICAL: Timestamp Validation
       - You MUST verify that all timestamps are within the recording's duration
       - NEVER cite a timestamp that exceeds the video length
       - If you encounter an invalid timestamp:
         1. Use the closest valid timestamp before that point
         2. If unavailable, use the earliest valid timestamp
         3. Flag any systematic timestamp issues in your response
       
       Citation Format Rules:
       - Use [→] as the link text to indicate a source reference
       - Citations must follow the format: [→](file_id@seconds)
         Examples:
         ✓ [→](abc123@90)
         ✓ [→](def456@3600)
         ❌ [→](Q4 Meeting@90)
         ❌ [→](Project Discussion@3600)
       
       ⚠️ Timestamp Sanity Checks:
       - Most business recordings are 15-90 minutes
       - Timestamps > 7200 (2 hours) are suspicious
       - Timestamps > 14400 (4 hours) are likely errors
       - If you see suspiciously high timestamps:
         1. Check if they're reasonable for the content
         2. Look for patterns suggesting systematic errors
         3. Use earlier, verified timestamps when in doubt
       
       Speaker Identification Rules:
       - NEVER use labels like "SPEAKER_01", "SPEAKER_1", "Speaker A", etc.
       - Instead, always identify speakers by:
         1. Role and name if known: "Product Manager Sarah"
         2. Role if name unknown: "Product Manager"
         3. Descriptive characteristics if role unknown:
            - "Female Customer"
            - "Male Engineer"
            - "Senior Team Member"
            - "Junior Developer"
       - Be consistent with speaker identification throughout
       - If multiple speakers share a role, differentiate them:
         - "First Engineer"
         - "Second Engineer"
         - "Engineering Lead"
         - "Engineering Team Member"

    6. Example Output with Citations:
       ### Poor Citations (Never Use):
       ❌ "The timeline is discussed" [→](Q4 Strategy Meeting@90)
       ❌ "Michael suggests" [→](Team Discussion@3600)
       
       ### Correct Citations:
       ✓ "The timeline is discussed" [→](abc123@90)
       ✓ "Michael suggests" [→](def456@3600)

    7. Quality and Validation Requirements:
       - VERIFY all timestamps before citing them (but do not mention this verification in output)
       - Silently validate all timestamps and only mention if there are actual issues
       - Ensure timestamps are logically sequential
       - Check that cited moments actually contain relevant content
       - Maintain chronological consistency in citations
       - If timestamp issues are detected:
         1. Note the issue in your response
         2. Use alternative valid timestamps
         3. Suggest reviewing the recording's timing data
       - Ensure factual accuracy
       - Present information clearly and concisely
       - Balance detail with readability
       - Preserve original terminology
       - Identify and consistently refer to speakers by role/title/name
       - Avoid meta-commentary about the analysis process or validation steps
       - Focus on actionable insights
       - Maintain clear logical flow
       - Include source citations for all key points
       - Maintain consistent recording identification throughout
       - Choose clear, meaningful names for recordings
       - Use the same identifier every time you reference a specific recording
  </guidelines>
</prompt>

${transcriptText}`;
};

export const SUGGESTIONS = [
  'Summarize Commonalities',
  'Get Action Items',
  'Create a Report',
  'Find Problems',
  'List Goals',
];
